import { useMemo } from "react";
import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { RoleS, normalizeRole } from "@ugg/shared/utils/role-helpers";
import { comparePatch, normalizePatch } from "@ugg/shared/utils/patch-helpers";

// ─────────────────────────────────────────────────────────────────────
// League Classic shared data layer
// ─────────────────────────────────────────────────────────────────────
//
// Endpoint reference: docs/lol-custom-data-feeds.md (§5). Update it when
// you add, move, or retire a feed here.
//
// Types, cleaners, image-URL helpers, and JSONFetcher hooks for the
// League Classic custom data pipeline. Modeled on the ARAM Mayhem data
// layer (see hooks/aram-mayhem.ts): data-only, no UI, so both the
// tier-list surface and any future Classic consumers (champion pages,
// SEO blocks, etc.) can share the same fetch layer + cache slots.
//
// Classic champions are NOT part of the standard riot-assets pipeline,
// so their portraits, ability icons, and champion JSON live under a
// dedicated CDN base rather than resolving through @outplayed/riot-assets.
// The helpers below build those URLs; only the champion manifest is
// actually consumed by the tier list today, but the rest are provided so
// other features can hang off the same base without re-deriving it.

// Root of the League Classic custom cdragon pipeline. Every category
// (champions, masteries, runes, spells, items) hangs off a per-version folder
// beneath this base. Kept separate from the standard riot-assets pipeline
// because none of this data is available through DDragon today — see the file
// header.
//
//   <CLASSIC_CDRAGON_BASE_URL>/<version>/<category>/<file>
//
// This replaced a single unversioned "league_classic" slug, which was the
// initial data setup. Nothing outside this file should build a Classic asset
// URL by hand — the version has to be resolved (see below), so the exported
// getters are the only supported entry points.
export const CLASSIC_CDRAGON_BASE_URL = "https://static.bigbrain.gg/cdragon-custom/classic";

// PATCH ROLLOVER — see also CLASSIC_PATCH_OPTION.
//
// The oldest patch with a published asset folder. Versioned assets start at
// 16_15; the pipeline's unversioned era covers everything before it, and those
// folders were not backfilled. So any patch older than this has no assets at
// all, and passing one through unclamped would 404 every portrait, icon and
// catalog on the page. Older selections resolve to this floor instead, which is
// exactly what they rendered when assets were unversioned.
//
// Whether the clamp can actually fire depends on the patch filter's window: it
// offers the newest CLASSIC_PATCH_OPTION.max patches, so it only reaches below
// the floor while latest is close to it. At latest = 16_15 the window is
// [16_15, 16_14] and 16_14 clamps; from 16_16 on, the window no longer includes
// anything below 16_15 and this becomes a pure backstop. Keep it either way —
// it costs nothing and it's what makes a hand-edited `?patch=` harmless.
//
// Only touch this if the pipeline starts deleting old version folders (raise
// it) or backfills earlier ones (lower it, or drop the clamp entirely).
//
// Deliberately no ceiling: if u.gg rolls to a patch before the Classic asset
// pipeline publishes its folder, assets 404 and the page visibly breaks. A
// "latest known good" ceiling would mask that by silently pinning every
// surface to a stale version until someone remembered to bump it — the same
// failure the unversioned slug had. A loud 404 on rollover day is the cheaper
// bug. The curated feeds under CLASSIC_CUSTOM_LEAGUE_BASE_URL already behave
// this way (missing patch → empty state).
export const CLASSIC_ASSET_VERSION_FLOOR = "16_15";

// Resolve a patch to the asset-path version segment. Undefined in → undefined
// out, so callers can hold off fetching until they know the patch rather than
// guessing a version and re-fetching when the real one arrives.
export function resolveClassicAssetVersion(patch: string | undefined): string | undefined {
  if (!patch) return undefined;
  // Normalize first so a dotted patch ("16.15") compares and renders the same as
  // the wire form. An input that doesn't come out as season_minor is unparseable
  // — floor it rather than splice it into a path that is certain to 404.
  // (In practice `patch` is a validated param or a useUGGPatches entry, so this
  // is a backstop, not an expected case.)
  const normalized = normalizePatch(patch);
  if (!/^\d+_\d+$/.test(normalized)) return CLASSIC_ASSET_VERSION_FLOOR;
  // comparePatch is season/week-numeric, not lexicographic — "16_9" vs "16_15"
  // would order wrong as strings.
  return comparePatch(normalized, CLASSIC_ASSET_VERSION_FLOOR) < 0 ? CLASSIC_ASSET_VERSION_FLOOR : normalized;
}

// Category folders under a resolved version. `version` is what
// resolveClassicAssetVersion returned, NOT a raw patch.
const classicAssetBase = (version: string, category: string) => `${CLASSIC_CDRAGON_BASE_URL}/${version}/${category}`;
export const getClassicChampionBaseUrl = (version: string) => classicAssetBase(version, "champions");
export const getClassicMasteriesBaseUrl = (version: string) => classicAssetBase(version, "masteries");
export const getClassicRunesBaseUrl = (version: string) => classicAssetBase(version, "runes");
export const getClassicSpellsBaseUrl = (version: string) => classicAssetBase(version, "spells");
export const getClassicItemsBaseUrl = (version: string) => classicAssetBase(version, "items");

// ─────────────────────────────────────────────────────────────────────
// JSON payload interfaces
// ─────────────────────────────────────────────────────────────────────

// classic-champions.json — flat manifest keyed by numeric champion key
// (as a string). Each value is a positional tuple, NOT an object:
//   [0] normalized id / alias ("annie")
//   [1] display name          ("Annie")
// Typed as a fixed-length tuple so [0]/[1] access is checked.
export type ClassicChampionEntry = [alias: string, displayName: string];
export type ClassicChampionsManifest = Record<string, ClassicChampionEntry>;

// Ability icon keys available per champion — q/w/e/r spells + p passive.
export type ClassicSkillKey = "q" | "w" | "e" | "r" | "p";

// Full role names for the role-letter shorthand used in the tier-list
// JSON. Keyed on the lowercase letter; used for icon alt/title text.
// Capitalized to match the site's role labels ("Middle", not "Mid").
//
// NOTE: a bespoke Classic role-icon set exists on the CDN at
// custom-league-classic/assets/tier-list-role/<letter>.webp, but the tier
// list renders the site's shared getRoleSVG glyphs instead so the icons
// match the filter chips. The URL builder for that set was removed as
// dead code; these names are still live (they supply the icon title).
export const CLASSIC_ROLE_NAMES: Record<string, string> = {
  t: "Top",
  j: "Jungle",
  m: "Middle",
  b: "Bot",
  s: "Support",
};

export function getClassicRoleName(role: string) {
  return CLASSIC_ROLE_NAMES[String(role ?? "").toLowerCase()] ?? role;
}

// Maps a site role (RoleS) to the role letter used in the tier-list JSON.
// Keyed on RoleS so it goes through normalizeRole below, which collapses
// the SEO "adc" value plus "bot"/"bottom" all onto RoleS.BOTTOM ("B") —
// that's how the filter supports bot/adc interchangeably.
const CLASSIC_ROLE_LETTER_BY_ROLE: Partial<Record<RoleS, string>> = {
  [RoleS.TOP]: "T",
  [RoleS.JUNGLE]: "J",
  [RoleS.MIDDLE]: "M",
  [RoleS.BOTTOM]: "B",
  [RoleS.SUPPORT]: "S",
};

// Resolves a role query value (top/jungle/mid/adc|bot|bottom/support, or
// the RoleS equivalents) to its tier-list role letter. Returns undefined
// for "all"/"none"/unknown — i.e. "don't filter by role".
export function getClassicRoleLetter(role: string | undefined | null): string | undefined {
  if (!role) return undefined;
  return CLASSIC_ROLE_LETTER_BY_ROLE[normalizeRole(role) as RoleS];
}

// Inverse of getClassicRoleLetter: a tier-list role letter (T/J/M/B/S, either
// case) → the site RoleS slug (top/jungle/mid/adc/support). Undefined for
// unknown letters. Used by the champion page to turn a tier entry's role into
// the slug the role selector + build URL expect.
export function getClassicRoleFromLetter(letter: string | undefined | null): RoleS | undefined {
  if (!letter) return undefined;
  const upper = letter.toUpperCase();
  return (Object.keys(CLASSIC_ROLE_LETTER_BY_ROLE) as RoleS[]).find((role) => CLASSIC_ROLE_LETTER_BY_ROLE[role] === upper);
}

// Champion tier list JSON. Same envelope as the ARAM Mayhem champion
// tier list, but each tier entry is a [championKey, roleLetter] tuple
// rather than a bare champion key — Classic carries a role association
// (T/J/M/B/S) that ARAM Mayhem did not.
export type ClassicTierEntry = [championKey: number, role: string];
export interface ClassicChampionTierList {
  // Partial: the payload only carries tiers that have champions, so an absent
  // tier is `undefined` (not `[]`). Indexing therefore forces callers to guard
  // — matching reality and preventing a `tiers["s-tier"].map(...)` crash.
  tiers: Partial<Record<TierNameValue, ClassicTierEntry[]>>;
  lastUpdated: string;
}

// ─────────────────────────────────────────────────────────────────────
// Image / asset URL helpers — plain string builders (no hooks) so they
// can be used anywhere, including outside React.
//
// Every one takes the resolved asset `version` (see
// resolveClassicAssetVersion) because the CDN path is versioned. It is
// intentionally required rather than defaulted: a default would let a call
// site silently pin to one version forever, which is the failure the
// unversioned slug had. Callers that don't know the version yet should not
// render the asset — see the components' `version &&` guards.
// ─────────────────────────────────────────────────────────────────────

export function getClassicChampionImgUrl(championKey: number | string, version: string) {
  return `${getClassicChampionBaseUrl(version)}/${championKey}.webp`;
}

export function getClassicChampionSkillImgUrl(championKey: number | string, skill: ClassicSkillKey, version: string) {
  return `${getClassicChampionBaseUrl(version)}/${championKey}_${skill}.webp`;
}

export function getClassicChampionJSONUrl(championKey: number | string, version: string) {
  return `${getClassicChampionBaseUrl(version)}/${championKey}.json`;
}

// ─────────────────────────────────────────────────────────────────────
// Cleaners — normalize the raw fetch payload, seeding empty defaults for
// the loading state so consumers can dereference without null-checks.
// ─────────────────────────────────────────────────────────────────────

// The manifest is a flat id→tuple dict (no envelope), so the cleaner just
// guards against the null/undefined the fetcher passes during loading.
export function cleanClassicChampions(data: ClassicChampionsManifest | null | undefined) {
  return data ?? ({} as ClassicChampionsManifest);
}

export function cleanClassicChampionTierList(data: ClassicChampionTierList | null | undefined) {
  const tierlist: Partial<Record<TierNameValue, ClassicTierEntry[]>> = data?.tiers ?? {};
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// ─────────────────────────────────────────────────────────────────────
// Fetch hooks — thin JSONFetcher wrappers around the CDN URLs.
// ─────────────────────────────────────────────────────────────────────

// Champion metadata manifest (id → [alias, displayName]). Now version-scoped
// like every other asset — pass the champion/tier-list page's patch and the
// hook resolves it. Skips until the patch is known so it can't fetch the floor
// version and then re-fetch the real one. Pass `ssr: true` to prefetch the
// names during server render so they land in the client's JSONFetcher cache on
// hydration (SSR handling mirrors the rest of the JSONFetcher hooks).
export function useClassicChampions<K = ReturnType<typeof cleanClassicChampions>>(
  options?: { patch?: string } & JSONFetcherOptions<ClassicChampionsManifest, K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const version = resolveClassicAssetVersion(patch);

  const url = `${getClassicChampionBaseUrl(version || "")}/classic-champions.json`;
  return useJSONFetcher<ClassicChampionsManifest, K>(url, {
    skip: skip || !version,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicChampions(json) as K),
    customCacheKey,
  });
}

// Per-champion info JSON (spells, stats, tactical info, ...). Not used by
// the tier list, but provided so downstream features (a Classic champion
// page, tooltips, etc.) can fetch a champion's full payload off the same
// base. Skips the fetch when championKey is missing/0 to avoid a
// malformed `.../undefined.json` request.
//
// Typed on the PAYLOAD generic (T) with no `onCompleted` transform, so the
// returned `data` is `T | undefined` — undefined during load and on a 404.
// (Threading the fetcher's second generic K instead would collapse the type
// to a non-optional `K`, hiding the loading/missing case and inviting an
// unguarded `champion.spells` access that type-checks but crashes at runtime.)
export function useClassicChampionJSON<T = ClassicChampionData>(
  options: { championKey: number | string; patch?: string } & JSONFetcherOptions<T>,
) {
  const { championKey, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const version = resolveClassicAssetVersion(patch);
  const effectiveSkip = skip || !championKey || !version;

  const url = getClassicChampionJSONUrl(championKey, version || "");
  return useJSONFetcher<T>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Per-champion info JSON (${key}.json) payload shape
// ─────────────────────────────────────────────────────────────────────
//
// The champion JSON carries everything the profile page needs beyond the
// portrait: the playstyle ratings, tag/role labels, the shortBio quick
// facts, and the full spell list (with a hand-authored dynamicDescription
// used to build the on-hover skill tooltips). Fields we don't consume yet
// (effectAmounts, ammo, coefficients) are typed loosely on purpose.

export interface ClassicChampionSpellData {
  spellKey: "q" | "w" | "e" | "r";
  name: string;
  cost: string;
  cooldown: string;
  description: string;
  // Hand-authored tooltip body. Contains custom color tags (<magicDamage>…),
  // <br> breaks, and @Placeholder@ tokens; unresolved tokens render as "?".
  dynamicDescription: string;
  range: number[];
  costCoefficients: number[];
  cooldownCoefficients: number[];
  coefficients: { coefficient1: number; coefficient2: number };
  effectAmounts: Record<string, number[]>;
  ammo: { ammoRechargeTime: number[]; maxAmmo: number[] };
  maxLevel: number;
}

export interface ClassicChampionData {
  id: number;
  relatedPrimeItemId: number;
  name: string;
  alias: string;
  title: string;
  // HTML string of "- fact<br>- fact" quick notes shown in Champion Info.
  shortBio: string;
  isVisibleInClient: boolean;
  tacticalInfo: { style: number; difficulty: number; damageType: string; attackType: string };
  // 0–3 ratings driving the Champion Info bars.
  playstyleInfo: { damage: number; durability: number; crowdControl: number; mobility: number; utility: number };
  championTagInfo: { championTagPrimary: string; championTagSecondary: string };
  roles: string[];
  recommendedItemDefaults: unknown[];
  passive: { name: string; description: string };
  spells: ClassicChampionSpellData[];
}

// ─────────────────────────────────────────────────────────────────────
// Patch-versioned curated data
// ─────────────────────────────────────────────────────────────────────
//
// The hand-curated per-champion builds live in a DIFFERENT bucket from the
// catalogs above — alongside the tier list, under a `<patch>/` segment. Both
// roots are patch-keyed now, but they version independently and are not
// guaranteed to carry the same patches, so never reuse one's resolved version
// for the other: these take the validated patch directly, the catalogs above go
// through resolveClassicAssetVersion. Most champions have no curated file yet,
// so most of these fetches 404 — callers treat that as "no curated data yet"
// and render empty slots.
export const CLASSIC_CUSTOM_LEAGUE_BASE_URL = "https://static.bigbrain.gg/custom-league-classic";

// ── Skill priority (compact per-champion format) ──
// A flat array of ability keys, e.g. ["Q","W","E"], keyed by champion key in
// a `mini/` subfolder of the patch-versioned skills dir:
//   <CLASSIC_CUSTOM_LEAGUE_BASE_URL>/<patch>/skills/mini/<championKey>.json
//
// There is also a `skills/<championKey>.json` sibling carrying priority PLUS
// the full per-level leveling path. Nothing upstream produces it — it 404s on
// every patch — so its fetcher was removed as dead code. If we resurface the
// leveling path, the feed has to be produced first.
export type ClassicChampionSkillsMini = string[];

// Skips the fetch when championKey or patch is missing (avoids a malformed URL
// during the loading window before patches resolve).
export function useClassicChampionSkillsMini<K = ClassicChampionSkillsMini>(
  options: { championKey: number | string; patch: string | undefined } & JSONFetcherOptions<ClassicChampionSkillsMini, K>,
) {
  const { championKey, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !championKey || !patch;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/skills/mini/${championKey}.json`;
  return useJSONFetcher<ClassicChampionSkillsMini, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Mastery manifest (jade-mastery-display.json) — the three trees rendered
// by the Masteries module, plus id/on-off icons keyed by mastery id.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicMasteryNode {
  id: number;
  name: string;
  rowIndex: number;
  maxRank: number;
  description: string;
  requiresMastery?: number;
}
// A row holds a fixed set of columns; `null` marks an empty cell so the grid
// keeps its column alignment.
export interface ClassicMasteryRow {
  pointsRequired: number;
  masteries: Array<ClassicMasteryNode | null>;
}
export interface ClassicMasteryTree {
  name: string;
  type: string; // "offense" | "defense" | "utility"
  rows: ClassicMasteryRow[];
}
export interface ClassicMasteryDisplay {
  trees: ClassicMasteryTree[];
}

// The selected-mastery map (mastery id → points allocated) is what both the
// Masteries module (on/off icons + rank badges) and the Mastery Summary
// (tooltips) read. One curated tree per champion — see useClassicChampionMastery.

export function getClassicMasteryIconUrl(masteryId: number | string, active: boolean, version: string) {
  return `${getClassicMasteriesBaseUrl(version)}/${masteryId}_${active ? "on" : "off"}.webp`;
}

// Per-tree background art for the Masteries panels (already tinted red/blue/green
// in the source). Keyed by tree type: offense/defense/utility.
export const CLASSIC_MASTERY_TREE_BG_BASE_URL = "https://static.bigbrain.gg/custom-league-classic/assets/mastery";
export function getClassicMasteryTreeBgUrl(type: string) {
  return `${CLASSIC_MASTERY_TREE_BG_BASE_URL}/${type.toLowerCase()}.webp`;
}

// ─── Curated mastery tree (one file per champion) ────────────────────
//
// Distinct from the mastery DISPLAY tree above (which defines the grid): each
// champion has a hand-authored point allocation in the PATCH-VERSIONED custom
// bucket (like skills), one file keyed by champion key:
//   <CLASSIC_CUSTOM_LEAGUE_BASE_URL>/<patch>/mastery/<championKey>.json
// The file groups id/rank pairs by category; the module only needs the selected
// ids, so we flatten to a { id: rank } map and treat any rank > 0 as "on". Not
// every champion has a file yet — a missing one (404) yields an empty selection,
// so the tree renders with no allocations (there is no fallback build).
export interface ClassicMasteryBuildFile {
  masteries: {
    offense: Array<[number, number]>;
    defense: Array<[number, number]>;
    utility: Array<[number, number]>;
  };
  lastUpdated?: string;
}

// Flatten the categorized id/rank pairs into the { id: rank } map the modules
// consume, keeping only allocated (rank > 0) nodes.
export function flattenClassicMasteryBuild(file: ClassicMasteryBuildFile | null | undefined): Record<number, number> {
  const selected: Record<number, number> = {};
  if (!file?.masteries) return selected;
  (["offense", "defense", "utility"] as const).forEach((category) => {
    const entries = file.masteries[category];
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      if (!Array.isArray(entry)) return;
      const [id, rank] = entry;
      if (rank > 0) selected[id] = rank;
    });
  });
  return selected;
}

// Fetch a champion's curated mastery tree and flatten it to the { id: rank }
// map the modules consume. Skips on missing patch/championKey (avoids a bad
// URL). A missing file (404) leaves `data` undefined → an empty selection, so
// the tree simply renders with nothing allocated (no fallback build).
export function useClassicChampionMastery(options: {
  championKey: string | number | undefined;
  patch: string | undefined;
  ssr?: boolean;
}): { selected: Record<number, number>; loading: boolean } {
  const { championKey, patch, ssr = false } = options;
  const effectiveSkip = !patch || !championKey;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/mastery/${championKey}.json`;
  const { data, loading } = useJSONFetcher<ClassicMasteryBuildFile>(url, { skip: effectiveSkip, ssr });

  const selected = useMemo(() => flattenClassicMasteryBuild(data), [data]);
  return { selected, loading };
}

// ─────────────────────────────────────────────────────────────────────
// Rune (perk) manifest (jade-perks.json). Icons are keyed by iconPath
// (e.g. "b_3_3.webp") rather than id because the art is shared across perks.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicPerk {
  id: number;
  type: string; // kMark | kSeal | kGlyph | kQuintessence
  statName: string;
  tooltip: string;
  title: string;
  amount: number;
  isPerLevel: boolean;
  isLowQuality: boolean;
  totalAmount?: number;
  stats: Record<string, number>;
  itemId: number;
  iconPath?: string;
  primaryStatGroup: string;
}

export function getClassicRuneIconUrl(iconPath: string, version: string) {
  return `${getClassicRunesBaseUrl(version)}/${iconPath}`;
}

// Curated rune build — the selected perks grouped by rune type, each entry a
// [perkId, count] pair (resolve names/effects/icons against the jade-perks.json
// manifest above). Like the skill/mastery/spell feeds, it lives in the
// PATCH-VERSIONED custom bucket:
//   <CLASSIC_CUSTOM_LEAGUE_BASE_URL>/<patch>/runes/<championKey>.json
// `name` is the champion key at every callsite today; the param is named
// generically because the pipeline originally addressed these by build name.
export interface ClassicRuneBuildFile {
  runes: {
    kQuintessence?: Array<[number, number]>;
    kMark?: Array<[number, number]>;
    kSeal?: Array<[number, number]>;
    kGlyph?: Array<[number, number]>;
  };
  lastUpdated?: string;
}

export function useClassicRuneBuild<K = ClassicRuneBuildFile>(
  options: { name: string; patch: string | undefined } & JSONFetcherOptions<ClassicRuneBuildFile, K>,
) {
  const { name, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !patch || !name;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/runes/${encodeURIComponent(name)}.json`;
  return useJSONFetcher<ClassicRuneBuildFile, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Summoner spell manifest (classic-spells.json). Icons keyed by spell id.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicSummonerSpell {
  id: number;
  name: string;
  description: string;
  cooldown: number;
}

export function getClassicSpellIconUrl(spellId: number | string, version: string) {
  return `${getClassicSpellsBaseUrl(version)}/${spellId}.webp`;
}

// Curated summoner-spell build — which two spells to recommend. Like the skill
// and mastery build feeds, it lives in the PATCH-VERSIONED custom bucket, one
// file per build named after the pairing:
//   <CLASSIC_CUSTOM_LEAGUE_BASE_URL>/<patch>/spells/<championKey>.json
// The file is just the ordered spell ids (resolve names/tooltips against the
// classic-spells.json manifest above). `name` is the champion key at every
// callsite today — see the rune build hook for why it's named generically.
export interface ClassicSpellBuildFile {
  spells: number[];
  lastUpdated?: string;
}

export function useClassicSpellBuild<K = ClassicSpellBuildFile>(
  options: { name: string; patch: string | undefined } & JSONFetcherOptions<ClassicSpellBuildFile, K>,
) {
  const { name, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !patch || !name;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/spells/${encodeURIComponent(name)}.json`;
  return useJSONFetcher<ClassicSpellBuildFile, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Item manifests. Two files: the full catalog (classic-items.json, ~110kb,
// carries tooltip HTML + recipe trees) and a lightweight id→name lookup
// (classic-items-mini.json) so icons/names render before the big file
// lands. Icons keyed by item id.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicItem {
  id: number;
  name: string;
  description: string;
  active: boolean;
  inStore: boolean;
  from: number[];
  to: number[];
  categories: string[];
  maxStacks: number;
  requiredChampion: string;
  requiredAlly: string;
  requiredBuffCurrencyName: string;
  requiredBuffCurrencyCost: number;
  specialRecipe: number;
  isEnchantment: boolean;
  price: number;
  priceTotal: number;
  displayInItemSets: boolean;
  priceCalculated: number;
  inShopList: boolean;
}
export type ClassicItemsMini = Record<string, string>;

export function getClassicItemIconUrl(itemId: number | string, version: string) {
  return `${getClassicItemsBaseUrl(version)}/${itemId}.webp`;
}

// Curated item build. Like the skill/mastery/spell/rune feeds, it lives in the
// PATCH-VERSIONED custom bucket:
//   <CLASSIC_CUSTOM_LEAGUE_BASE_URL>/<patch>/items/<championKey>.json
// `starter` is the opening buy (duplicate ids = a stacked consumable, e.g. a
// potion x2); `endgame` is the finished build (≤ 6 items, enforced upstream);
// `situational` is the optional set of matchup-dependent swaps.
// Resolve names/tooltips/icons against the mini + full item manifests above.
export interface ClassicItemBuildFile {
  items: {
    starter: number[];
    endgame: number[];
    situational?: number[];
  };
  lastUpdated?: string;
}

export function useClassicItemBuild<K = ClassicItemBuildFile>(
  options: { name: string; patch: string | undefined } & JSONFetcherOptions<ClassicItemBuildFile, K>,
) {
  const { name, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !patch || !name;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/items/${encodeURIComponent(name)}.json`;
  return useJSONFetcher<ClassicItemBuildFile, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Cleaners for the new manifests. Each seeds an empty default so consumers
// can dereference during loading without null-checks.
// ─────────────────────────────────────────────────────────────────────

// Array.isArray (not just `?? []`): if a payload arrives wrong-typed (an object
// where an array is expected), return empty so the type stays honest and no
// downstream `.map`/`.forEach` can throw — empty beats a crash.
export function cleanClassicMasteryDisplay(data: ClassicMasteryDisplay | null | undefined) {
  return { trees: Array.isArray(data?.trees) ? data!.trees : [] } as ClassicMasteryDisplay;
}
export function cleanClassicPerks(data: ClassicPerk[] | null | undefined) {
  return Array.isArray(data) ? data : [];
}
export function cleanClassicSpells(data: ClassicSummonerSpell[] | null | undefined) {
  return Array.isArray(data) ? data : [];
}
export function cleanClassicItems(data: ClassicItem[] | null | undefined) {
  return Array.isArray(data) ? data : [];
}
export function cleanClassicItemsMini(data: ClassicItemsMini | null | undefined) {
  return data ?? ({} as ClassicItemsMini);
}

// ─────────────────────────────────────────────────────────────────────
// Fetch hooks for the remaining catalogs. All four are version-scoped: they
// live under <CLASSIC_CDRAGON_BASE_URL>/<version>/<category>/, so each takes
// the page's `patch` and skips until it's known (see useClassicChampions for
// why guessing a version instead would double-fetch). `ssr: true` prefetches
// during server render for SEO, mirroring useClassicChampions.
// ─────────────────────────────────────────────────────────────────────

export function useClassicMasteryDisplay<K = ReturnType<typeof cleanClassicMasteryDisplay>>(
  options?: { patch?: string } & JSONFetcherOptions<ClassicMasteryDisplay, K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const version = resolveClassicAssetVersion(patch);
  const url = `${getClassicMasteriesBaseUrl(version || "")}/jade-mastery-display.json`;
  return useJSONFetcher<ClassicMasteryDisplay, K>(url, {
    skip: skip || !version,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicMasteryDisplay(json) as K),
    customCacheKey,
  });
}

export function useClassicPerks<K = ReturnType<typeof cleanClassicPerks>>(
  options?: { patch?: string } & JSONFetcherOptions<ClassicPerk[], K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const version = resolveClassicAssetVersion(patch);
  const url = `${getClassicRunesBaseUrl(version || "")}/jade-perks.json`;
  return useJSONFetcher<ClassicPerk[], K>(url, {
    skip: skip || !version,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicPerks(json) as K),
    customCacheKey,
  });
}

export function useClassicSpells<K = ReturnType<typeof cleanClassicSpells>>(
  options?: { patch?: string } & JSONFetcherOptions<ClassicSummonerSpell[], K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const version = resolveClassicAssetVersion(patch);
  const url = `${getClassicSpellsBaseUrl(version || "")}/classic-spells.json`;
  return useJSONFetcher<ClassicSummonerSpell[], K>(url, {
    skip: skip || !version,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicSpells(json) as K),
    customCacheKey,
  });
}

export function useClassicItems<K = ReturnType<typeof cleanClassicItems>>(
  options?: { patch?: string } & JSONFetcherOptions<ClassicItem[], K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const version = resolveClassicAssetVersion(patch);
  const url = `${getClassicItemsBaseUrl(version || "")}/classic-items.json`;
  return useJSONFetcher<ClassicItem[], K>(url, {
    skip: skip || !version,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicItems(json) as K),
    customCacheKey,
  });
}

// Lightweight id→name lookup. Fetch this alongside the icons for fast
// first paint; the full useClassicItems payload (tooltips/recipes) can
// resolve afterwards without blocking the initial render.
export function useClassicItemsMini<K = ReturnType<typeof cleanClassicItemsMini>>(
  options?: { patch?: string } & JSONFetcherOptions<ClassicItemsMini, K>,
) {
  const { patch, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const version = resolveClassicAssetVersion(patch);
  const url = `${getClassicItemsBaseUrl(version || "")}/classic-items-mini.json`;
  return useJSONFetcher<ClassicItemsMini, K>(url, {
    skip: skip || !version,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicItemsMini(json) as K),
    customCacheKey,
  });
}

// Champion tier list per patch. Consumed by both the tier-list page and
// the champion build page (which derives a champion's core role and
// per-role tier from it). Note the "-v1" filename suffix — the ARAM
// Mayhem equivalent has none:
//   <CLASSIC_CUSTOM_LEAGUE_BASE_URL>/<patch>/tierlist-champions-<patch>-v1.json
// with `patch` resolved via the CLASSIC_TIER_LIST page params (the patch
// filter swaps the version segment).
export function useClassicChampionTierList<K = ReturnType<typeof cleanClassicChampionTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<ClassicChampionTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.CLASSIC_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const effectiveSkip = skip || !patch;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/tierlist-champions-${patch}-v1.json`;
  return useJSONFetcher<ClassicChampionTierList, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicChampionTierList(json) as K),
    customCacheKey,
  });
}
